import { Plus, X } from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

interface Tag {
  id: string
  name: string
}

type Level = 'ENTRY' | 'BASIC' | 'ADVANCED' | 'EXPERT'

export interface LevelTagCombination {
  id: string
  tagId: string
  level: Level
}

interface LevelTagSelectorProps {
  value: LevelTagCombination[]
  onChange: (value: LevelTagCombination[]) => void
  availableTags: Tag[]
  onCreateTag?: (name: string) => Promise<Tag>
  className?: string
}

const LEVELS = [
  { value: 'ENTRY', label: 'ENTRY' },
  { value: 'BASIC', label: 'BASIC' },
  { value: 'ADVANCED', label: 'ADVANCED' },
  { value: 'EXPERT', label: 'EXPERT' },
]

export function LevelTagSelector({
  value,
  onChange,
  availableTags,
  className,
}: LevelTagSelectorProps) {
  const [newLevel, setNewLevel] = useState<Level>('ENTRY')
  const [newTagId, setNewTagId] = useState<string>('')

  const addCombination = () => {
    if (!newTagId) return

    // 同じタグ×レベルの重複を防止
    const duplicate = value.some((c) => c.tagId === newTagId && c.level === newLevel)
    if (duplicate) return

    const newCombination: LevelTagCombination = {
      id: Math.random().toString(36).substr(2, 9),
      tagId: newTagId,
      level: newLevel,
    }

    onChange([...value, newCombination])
    setNewTagId('')
  }

  const removeCombination = (id: string) => {
    onChange(value.filter((c) => c.id !== id))
  }

  // 未選択のタグのみ表示（同じタグ×レベルペアの重複防止用にフィルタしない — 同タグ別レベルはOK）
  const tagOptions = availableTags

  return (
    <div className={cn('space-y-4', className)}>
      {/* 既存の組み合わせ */}
      {value.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium">登録済みの組み合わせ</p>
          {value.map((combination) => {
            const tag = availableTags.find((t) => t.id === combination.tagId)
            return (
              <Card key={combination.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{combination.level}</Badge>
                    <span className="text-sm text-gray-600">×</span>
                    {tag && (
                      <Badge variant="secondary" className="text-xs">
                        {tag.name}
                      </Badge>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeCombination(combination.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* 新しい組み合わせの追加 */}
      <div className="space-y-3">
        <p className="text-sm font-medium">新しい組み合わせを追加</p>
        <div className="flex gap-2 items-end">
          <div className="flex-1 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div>
                <label className="text-xs text-gray-600">レベル</label>
                <Select value={newLevel} onValueChange={(v) => setNewLevel(v as Level)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-gray-600">タグ</label>
                <Select value={newTagId} onValueChange={setNewTagId}>
                  <SelectTrigger>
                    <SelectValue placeholder="タグを選択..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tagOptions.map((tag) => (
                      <SelectItem key={tag.id} value={tag.id}>
                        {tag.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <Button type="button" onClick={addCombination} disabled={!newTagId} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            追加
          </Button>
        </div>
      </div>
    </div>
  )
}
